import { useEffect, useState } from "react";
import { useAuth } from "src/contexts/auth-provider";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import { UPDATE_STORE_TIP_SETTINGS } from "src/constants/mutations";
import { GET_STORE_TIP_SETTINGS } from "src/constants/queries";
import { successToast, failureToast } from "src/utils/toasts";
import { BtnLoader } from "src/Loader";
import { StoreTipSettings, UpdateStoreTipSettingsInput } from "src/interfaces/tipSettings.interface";
import FormRowVertical from "src/components/common/FormRowVertical";
import Input, { InputTypeEnum } from "src/components/common/Input";

// Guardrail ranges (can be hardcoded or fetched from backend)
const GUARDRAILS = {
  percentage: { min: 15, max: 30, default: 30 },
  minDollar: { min: 2, max: 10, default: 3 },
  maxDollar: { min: 20, max: 100, default: 50 },
};

const TipAuthorizationSettings = () => {
  const { axBe } = useAxiosInterceptor();
  const { hasRestaurant } = useAuth();
  const [config, setConfig] = useState<StoreTipSettings | null>(null);
  const [loading, setLoading] = useState(false);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  
  // Form state
  const [percentage, setPercentage] = useState<number>(GUARDRAILS.percentage.default);
  const [minDollar, setMinDollar] = useState<number>(GUARDRAILS.minDollar.default);
  const [maxDollar, setMaxDollar] = useState<number>(GUARDRAILS.maxDollar.default);
  
  // Validation errors
  const [errors, setErrors] = useState<{
    percentage?: string;
    minDollar?: string;
    maxDollar?: string;
  }>({});

  useEffect(() => {
    fetchConfig();
  }, [hasRestaurant?.correlationId]);

  const fetchConfig = async () => {
    if (!hasRestaurant?.correlationId) return;

    setIsLoadingConfig(true);
    try {
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: GET_STORE_TIP_SETTINGS,
        variables: {
          retrieveStoresInput: {
            storeCorrelationIds: [hasRestaurant.correlationId],
          },
        },
      });

      if (data?.errors?.length) {
        throw data.errors;
      }

      const stores = data?.data?.stores || [];
      const storeData: StoreTipSettings | null = stores.length > 0 ? stores[0] : null;
      if (storeData?.correlationId) {
        setConfig(storeData);
        setPercentage(storeData.tipAuthBufferPercentage ?? GUARDRAILS.percentage.default);
        setMinDollar((storeData.tipAuthMinDollarCents ?? GUARDRAILS.minDollar.default * 100) / 100);
        setMaxDollar((storeData.tipAuthMaxDollarCents ?? GUARDRAILS.maxDollar.default * 100) / 100);
      }
    } catch (error: any) {
      console.error("Error fetching tip auth config:", error);
      const errorMessage = error?.message || error?.[0]?.message || "Failed to load tip authorization settings";
      failureToast(errorMessage);
    } finally {
      setIsLoadingConfig(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    // Validate percentage
    if (percentage < GUARDRAILS.percentage.min || percentage > GUARDRAILS.percentage.max) {
      newErrors.percentage = `Percentage must be between ${GUARDRAILS.percentage.min}% and ${GUARDRAILS.percentage.max}%`;
    }

    // Validate min dollar
    if (minDollar < GUARDRAILS.minDollar.min || minDollar > GUARDRAILS.minDollar.max) {
      newErrors.minDollar = `Min dollar must be between $${GUARDRAILS.minDollar.min} and $${GUARDRAILS.minDollar.max}`;
    }

    // Validate max dollar
    if (maxDollar < GUARDRAILS.maxDollar.min || maxDollar > GUARDRAILS.maxDollar.max) {
      newErrors.maxDollar = `Max dollar must be between $${GUARDRAILS.maxDollar.min} and $${GUARDRAILS.maxDollar.max}`;
    }

    // Validate min < max
    if (minDollar >= maxDollar) {
      newErrors.minDollar = "Min dollar must be less than max dollar";
      newErrors.maxDollar = "Max dollar must be greater than min dollar";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!hasRestaurant?.correlationId) {
      failureToast("No restaurant selected");
      return;
    }

    if (!validateForm()) {
      failureToast("Please fix validation errors");
      return;
    }

    setLoading(true);
    try {
      const input: UpdateStoreTipSettingsInput = {
        storeCorrelationId: hasRestaurant.correlationId,
        tipAuthBufferPercentage: percentage,
        tipAuthMinDollarCents: Math.round(minDollar * 100),
        tipAuthMaxDollarCents: Math.round(maxDollar * 100),
      };

      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: UPDATE_STORE_TIP_SETTINGS,
        variables: {
          updateStoreTipSettingsInput: input,
        },
      });

      if (data?.errors?.length) {
        throw data.errors;
      }

      successToast("Tip authorization settings saved successfully!");
      await fetchConfig();
    } catch (error: any) {
      console.error("Error saving tip auth config:", error);
      const errorMessage = error?.message || error?.[0]?.message || "Failed to save tip authorization settings";
      failureToast(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (isLoadingConfig) {
    return (
      <div className="bg-[#ffffff] rounded-[16px] px-6 py-5 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_rgba(0,0,0,0.04)] flex justify-center items-center py-12">
        <BtnLoader />
      </div>
    );
  }

  return (
    <div className="bg-[#ffffff] rounded-[16px] px-6 py-5 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_rgba(0,0,0,0.04)]">
      <h2 className="text-[18px] font-semibold text-[#111827] tracking-[-0.02em] mb-2">Tip Authorization Settings</h2>
      <p className="text-[14px] text-[#6b7280] mb-6">
        Configure how much extra to authorize for tips to avoid re-authorization. This allows customers to add tips without requiring a new authorization.
      </p>

        <div className="space-y-4">
          <FormRowVertical label={`Buffer Percentage (${GUARDRAILS.percentage.min}-${GUARDRAILS.percentage.max}%)`}>
            <div className="flex items-center gap-2">
              <Input
                name="percentage"
                type={InputTypeEnum.NUMBER}
                value={percentage}
                onChange={(e) => {
                  const value = parseFloat(e.target.value) || 0;
                  setPercentage(value);
                  if (errors.percentage) {
                    setErrors({ ...errors, percentage: undefined });
                  }
                }}
                min={String(GUARDRAILS.percentage.min)}
                max={String(GUARDRAILS.percentage.max)}
                className="w-32"
              />
              <span className="text-gray-600">%</span>
            </div>
            {errors.percentage && (
              <p className="text-red-500 text-xs mt-1">{errors.percentage}</p>
            )}
          </FormRowVertical>

          <FormRowVertical label={`Min Dollar Amount ($${GUARDRAILS.minDollar.min}-$${GUARDRAILS.minDollar.max})`}>
            <div className="flex items-center gap-2">
              <span className="text-gray-600">$</span>
              <Input
                name="minDollar"
                type={InputTypeEnum.NUMBER}
                step="0.01"
                value={minDollar.toFixed(2)}
                onChange={(e) => {
                  const value = parseFloat(e.target.value) || 0;
                  setMinDollar(value);
                  if (errors.minDollar) {
                    setErrors({ ...errors, minDollar: undefined });
                  }
                }}
                min={String(GUARDRAILS.minDollar.min)}
                max={String(GUARDRAILS.minDollar.max)}
                className="w-32"
              />
            </div>
            {errors.minDollar && (
              <p className="text-red-500 text-xs mt-1">{errors.minDollar}</p>
            )}
          </FormRowVertical>

          <FormRowVertical label={`Max Dollar Amount ($${GUARDRAILS.maxDollar.min}-$${GUARDRAILS.maxDollar.max})`}>
            <div className="flex items-center gap-2">
              <span className="text-gray-600">$</span>
              <Input
                name="maxDollar"
                type={InputTypeEnum.NUMBER}
                step="0.01"
                value={maxDollar.toFixed(2)}
                onChange={(e) => {
                  const value = parseFloat(e.target.value) || 0;
                  setMaxDollar(value);
                  if (errors.maxDollar) {
                    setErrors({ ...errors, maxDollar: undefined });
                  }
                }}
                min={String(GUARDRAILS.maxDollar.min)}
                max={String(GUARDRAILS.maxDollar.max)}
                className="w-32"
              />
            </div>
            {errors.maxDollar && (
              <p className="text-red-500 text-xs mt-1">{errors.maxDollar}</p>
            )}
          </FormRowVertical>

        </div>

      <div className="flex justify-end mt-6">
        {loading ? (
          <BtnLoader />
        ) : (
          <button
            type="button"
            onClick={handleSave}
            disabled={loading}
            className="px-5 py-2.5 rounded-xl bg-[#FD7657] text-white text-sm font-semibold hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-[#FD7657]/20 focus:ring-offset-2 disabled:opacity-50"
          >
            Save
          </button>
        )}
      </div>
    </div>
  );
};

export default TipAuthorizationSettings;

